import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { ArrowLeft, Check, X, Ban, AlertCircle, Car, FileText, Image, Key, User } from 'lucide-react';
import { getUserDetails, updateUserVerification, suspendUser, unsuspendUser, resetUserPassword } from '../../services/admin/userService';
import { toast } from '../../hooks/use-toast';

const UserDetails = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [showSuspendModal, setShowSuspendModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [suspensionDays, setSuspensionDays] = useState(7);
  const [suspensionReason, setSuspensionReason] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  useEffect(() => {
    loadUserDetails();
  }, [userId]);

  const loadUserDetails = async () => {
    setLoading(true);
    const result = await getUserDetails(userId);
    
    if (result.success) {
      setUser(result.data.user);
    } else {
      toast({ title: 'Error', description: result.message, variant: 'destructive' });
      navigate('/admin/users');
    }
    
    setLoading(false);
  };

  const handleVerify = async () => {
    setActionLoading(true);
    const result = await updateUserVerification(userId, 'verified');
    
    if (result.success) {
      toast({ title: 'Success', description: 'User verified successfully' });
      loadUserDetails();
    } else {
      toast({ title: 'Error', description: result.message, variant: 'destructive' });
    }
    
    setActionLoading(false);
  };

  const handleReject = async () => {
    const reason = prompt('Enter rejection reason:');
    if (!reason) return;

    setActionLoading(true);
    const result = await updateUserVerification(userId, 'rejected', reason);
    
    if (result.success) {
      toast({ title: 'Success', description: 'User verification rejected' });
      loadUserDetails();
    } else {
      toast({ title: 'Error', description: result.message, variant: 'destructive' });
    }
    
    setActionLoading(false);
  };

  const handleSuspend = async () => {
    if (!suspensionReason.trim()) {
      toast({ title: 'Error', description: 'Please provide a reason', variant: 'destructive' });
      return;
    }

    setActionLoading(true);
    const result = await suspendUser(userId, suspensionDays, suspensionReason);
    
    if (result.success) {
      toast({ title: 'Success', description: 'User suspended successfully' });
      setShowSuspendModal(false);
      setSuspensionReason('');
      loadUserDetails();
    } else {
      toast({ title: 'Error', description: result.message, variant: 'destructive' });
    }
    
    setActionLoading(false);
  };

  const handleUnsuspend = async () => {
    setActionLoading(true);
    const result = await unsuspendUser(userId);
    
    if (result.success) {
      toast({ title: 'Success', description: 'User unsuspended successfully' });
      loadUserDetails();
    } else {
      toast({ title: 'Error', description: result.message, variant: 'destructive' });
    }
    
    setActionLoading(false);
  };


  const handleResetPassword = async () => {
    // Validate passwords
    if (newPassword.length < 6) {
      toast({ title: 'Error', description: 'Password must be at least 6 characters', variant: 'destructive' });
      return;
    }

    if (newPassword !== confirmPassword) {
      toast({ title: 'Error', description: 'Passwords do not match', variant: 'destructive' });
      return;
    }

    setActionLoading(true);
    const result = await resetUserPassword(userId, newPassword);
    
    if (result.success) {
      toast({ 
        title: 'Success', 
        description: 'Password reset successfully. User can now login with the new password.' 
      });
      setShowPasswordModal(false);
      setNewPassword('');
      setConfirmPassword('');
    } else {
      toast({ title: 'Error', description: result.message, variant: 'destructive' });
    }
    
    setActionLoading(false);
  };


  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading user details...</p>
        </div>
      </div>
    );
  }

  if (!user) return null;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button onClick={() => navigate('/admin/users')} variant="outline" size="sm">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        <div className="flex-1 flex items-center gap-4">
          {/* Driver Face Photo */}
          {user.role === 'driver' && user.face_photo_url && (
            <div className="relative">
              <img
                src={`${process.env.REACT_APP_BACKEND_URL}${user.face_photo_url}`}
                alt={`${user.first_name} ${user.last_name}`}
                className="w-20 h-20 rounded-full object-cover border-4 border-green-500 cursor-pointer hover:border-green-600 transition-colors"
                onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}${user.face_photo_url}`, '_blank')}
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="80" height="80"%3E%3Crect fill="%23f3f4f6" width="80" height="80" rx="40"/%3E%3Ctext fill="%236b7280" font-family="sans-serif" font-size="24" x="50%25" y="50%25" text-anchor="middle" dominant-baseline="middle"%3E{user.first_name.charAt(0)}{user.last_name.charAt(0)}%3C/text%3E%3C/svg%3E';
                }}
              />
              <div className="absolute -bottom-1 -right-1 bg-green-500 text-white rounded-full p-1">
                <User className="w-3 h-3" />
              </div>
            </div>
          )}
          <div>
            <h2 className="text-2xl font-bold text-gray-800">
              {user.first_name} {user.middle_name} {user.last_name}
            </h2>
            <p className="text-gray-600">{user.role} Profile</p>
          </div>
        </div>
      </div>

      {/* User Info */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="p-6 lg:col-span-2">
          <h3 className="text-lg font-semibold mb-4">Personal Information</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-600">Full Name</p>
              <p className="font-medium">{user.first_name} {user.middle_name || ''} {user.last_name}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Role</p>
              <Badge className={user.role === 'driver' ? 'bg-purple-500' : 'bg-blue-500'}>
                {user.role}
              </Badge>
            </div>
            {user.role === 'driver' && user.driver_type && (
              <div>
                <p className="text-sm text-gray-600">Driver Type</p>
                <Badge className={user.driver_type === 'bike' ? 'bg-orange-500' : 'bg-purple-500'}>
                  {user.driver_type === 'bike' ? 'Bike Driver' : 'Vehicle Driver'}
                </Badge>
              </div>
            )}
            <div>
              <p className="text-sm text-gray-600">Phone</p>
              <p className="font-medium">{user.country_code} {user.phone}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Email</p>
              <p className="font-medium">{user.email || 'Not provided'}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Phone Verified</p>
              <Badge className={user.phone_verified ? 'bg-green-500' : 'bg-red-500'}>
                {user.phone_verified ? 'Yes' : 'No'}
              </Badge>
            </div>
            <div>
              <p className="text-sm text-gray-600">Created At</p>
              <p className="font-medium">{new Date(user.created_at).toLocaleDateString()}</p>
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <h3 className="text-lg font-semibold mb-4">Status</h3>
          <div className="space-y-3">
            <div>
              <p className="text-sm text-gray-600 mb-1">Verification Status</p>
              <Badge className={
                user.verification_status === 'verified' ? 'bg-green-500' :
                user.verification_status === 'rejected' ? 'bg-red-500' :
                user.verification_status === 'in_progress' ? 'bg-blue-500' :
                'bg-yellow-500'
              }>
                {user.verification_status}
              </Badge>
            </div>
            <div>
              <p className="text-sm text-gray-600 mb-1">Account Status</p>
              <Badge className={user.is_suspended ? 'bg-red-500' : 'bg-green-500'}>
                {user.is_suspended ? 'Suspended' : 'Active'}
              </Badge>
            </div>
            {user.is_suspended && user.suspension_end_date && (
              <div>
                <p className="text-sm text-gray-600 mb-1">Suspension Ends</p>
                <p className="text-sm">{new Date(user.suspension_end_date).toLocaleDateString()}</p>
              </div>
            )}
            {user.suspension_reason && (
              <div>
                <p className="text-sm text-gray-600 mb-1">Reason</p>
                <p className="text-sm">{user.suspension_reason}</p>
              </div>
            )}
          </div>
        </Card>
      </div>

      {/* Vehicle Info (for drivers) */}
      {user.vehicle && (
        <Card className="p-6">
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Car className="w-5 h-5" />
            Vehicle Information
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <p className="text-sm text-gray-600">Type</p>
              <Badge className={
                user.vehicle.type === 'bike' ? 'bg-orange-500' : 'bg-purple-500'
              }>
                {user.vehicle.type === 'bike' ? 'Bike' : 'Vehicle'}
              </Badge>
            </div>
            <div>
              <p className="text-sm text-gray-600">Category</p>
              <Badge className={
                user.vehicle.category === 'premium' ? 'bg-blue-500' : 
                user.vehicle.category === 'bike' ? 'bg-orange-500' : 
                'bg-green-500'
              }>
                {user.vehicle.category ? user.vehicle.category.toUpperCase() : 'N/A'}
              </Badge>
            </div>
            <div>
              <p className="text-sm text-gray-600">Make</p>
              <p className="font-medium">{user.vehicle.make}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Model</p>
              <p className="font-medium">{user.vehicle.model}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Plate Number</p>
              <p className="font-medium">{user.vehicle.plate_number}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Color</p>
              <p className="font-medium">{user.vehicle.color}</p>
            </div>
            {user.vehicle.year && (
              <div>
                <p className="text-sm text-gray-600">Year</p>
                <p className="font-medium">{user.vehicle.year}</p>
              </div>
            )}
            <div>
              <p className="text-sm text-gray-600">Status</p>
              <Badge className={user.vehicle.is_verified ? 'bg-green-500' : 'bg-yellow-500'}>
                {user.vehicle.is_verified ? 'Verified' : 'Pending'}
              </Badge>
            </div>
          </div>
          
          {/* Vehicle Images Section */}
          {user.vehicle.type === 'vehicle' && (
            <div className="mt-6">
              <p className="text-sm font-medium text-gray-700 mb-3">Vehicle Images</p>
              {user.vehicle.images && Object.keys(user.vehicle.images).length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {['front', 'side', 'back'].map((position) => (
                    <div key={position} className="relative">
                      {user.vehicle.images[position] ? (
                        <>
                          <img
                            src={`${process.env.REACT_APP_BACKEND_URL}${user.vehicle.images[position]}`}
                            alt={`Vehicle ${position}`}
                            className="w-full h-40 object-cover rounded-lg border-2 border-gray-200 cursor-pointer hover:border-green-500 transition-colors"
                            onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}${user.vehicle.images[position]}`, '_blank')}
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="200" height="200"%3E%3Crect fill="%23f3f4f6" width="200" height="200"/%3E%3Ctext fill="%236b7280" font-family="sans-serif" font-size="14" x="50%25" y="50%25" text-anchor="middle" dominant-baseline="middle"%3EImage not found%3C/text%3E%3C/svg%3E';
                            }}
                          />
                          <p className="text-xs text-center mt-2 capitalize font-medium text-gray-700">{position} View</p>
                        </>
                      ) : (
                        <div className="w-full h-40 bg-gray-100 rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center">
                          <div className="text-center text-gray-400">
                            <Image className="w-8 h-8 mx-auto mb-1" />
                            <p className="text-xs capitalize">{position} - Not uploaded</p>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-center">
                  <AlertCircle className="w-6 h-6 text-yellow-600 mx-auto mb-2" />
                  <p className="text-sm text-yellow-800">No vehicle images uploaded</p>
                </div>
              )}
            </div>
          )}
        </Card>
      )}

      {/* Verification Documents */}
      {user.documents && user.documents.length > 0 && (
        <Card className="p-6">
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Verification Documents
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {user.documents.map((doc) => (
              <div key={doc.id} className="border rounded-lg p-3">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-medium capitalize">
                    {doc.document_type.replace(/_/g, ' ')}
                  </p>
                  <Badge className={
                    doc.verification_status === 'verified' ? 'bg-green-500' :
                    doc.verification_status === 'rejected' ? 'bg-red-500' :
                    'bg-yellow-500'
                  }>
                    {doc.verification_status}
                  </Badge>
                </div>
                {doc.document_url && (
                  <img
                    src={`${process.env.REACT_APP_BACKEND_URL}${doc.document_url}`}
                    alt={doc.document_type}
                    className="w-full h-40 object-cover rounded border cursor-pointer hover:opacity-80"
                    onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}${doc.document_url}`, '_blank')}
                  />
                )}
                <p className="text-xs text-gray-500 mt-2">
                  Uploaded: {new Date(doc.uploaded_at).toLocaleDateString()}
                </p>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Actions */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold mb-4">Actions</h3>
        <div className="flex flex-wrap gap-3">
          {user.verification_status !== 'verified' && (
            <Button
              onClick={handleVerify}
              disabled={actionLoading}
              className="bg-green-600 hover:bg-green-700 flex items-center gap-2"
            >
              <Check className="w-4 h-4" />
              Verify User
            </Button>
          )}
          
          {user.verification_status === 'pending' && (
            <Button
              onClick={handleReject}
              disabled={actionLoading}
              className="bg-red-600 hover:bg-red-700 flex items-center gap-2"
            >
              <X className="w-4 h-4" />
              Reject
            </Button>
          )}
          
          <Button
            onClick={() => setShowPasswordModal(true)}
            disabled={actionLoading}
            className="bg-purple-600 hover:bg-purple-700 flex items-center gap-2"
          >
            <Key className="w-4 h-4" />
            Reset Password
          </Button>
          
          {!user.is_suspended ? (
            <Button
              onClick={() => setShowSuspendModal(true)}
              disabled={actionLoading}
              className="bg-orange-600 hover:bg-orange-700 flex items-center gap-2"
            >
              <Ban className="w-4 h-4" />
              Suspend User
            </Button>
          ) : (
            <Button
              onClick={handleUnsuspend}
              disabled={actionLoading}
              className="bg-blue-600 hover:bg-blue-700 flex items-center gap-2"
            >
              <Check className="w-4 h-4" />
              Unsuspend User
            </Button>
          )}
        </div>
      </Card>

      {/* Reports */}
      {user.reports && user.reports.length > 0 && (
        <Card className="p-6">
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <AlertCircle className="w-5 h-5" />
            Reports ({user.reports.length})
          </h3>
          <div className="space-y-3">
            {user.reports.map((report, index) => (
              <div key={index} className="border-l-4 border-red-500 bg-red-50 p-3">
                <p className="font-medium">{report.reason}</p>
                <p className="text-sm text-gray-600">{new Date(report.created_at).toLocaleString()}</p>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Suspension Modal */}
      {showSuspendModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="max-w-md w-full p-6">
            <h3 className="text-lg font-semibold mb-4">Suspend User</h3>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium mb-2 block">Duration</label>
                <select
                  value={suspensionDays}
                  onChange={(e) => setSuspensionDays(Number(e.target.value))}
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                >
                  <option value={7}>7 Days</option>
                  <option value={14}>14 Days</option>
                  <option value={30}>1 Month</option>
                  <option value={90}>3 Months</option>
                  <option value={0}>Permanent</option>
                </select>
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">Reason</label>
                <textarea
                  value={suspensionReason}
                  onChange={(e) => setSuspensionReason(e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 min-h-24"
                  placeholder="Enter suspension reason..."
                />
              </div>
              <div className="flex gap-3">
                <Button
                  onClick={handleSuspend}
                  disabled={actionLoading}
                  className="flex-1 bg-orange-600 hover:bg-orange-700"
                >
                  Suspend
                </Button>
                <Button
                  onClick={() => {
                    setShowSuspendModal(false);
                    setSuspensionReason('');
                  }}
                  variant="outline"
                  className="flex-1"
                >
                  Cancel
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Password Reset Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="max-w-md w-full p-6">
            <div className="flex items-center gap-2 mb-4">
              <Key className="w-5 h-5 text-purple-600" />
              <h3 className="text-lg font-semibold">Reset User Password</h3>
            </div>
            
            <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-sm text-yellow-800">
                <strong>Warning:</strong> This will reset the password for {user.username || user.first_name}. 
                Make sure to securely communicate the new password to the user.
              </p>
            </div>

            <div className="space-y-4">
              <div>
                <Label htmlFor="newPassword">New Password</Label>
                <Input
                  id="newPassword"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password"
                  className="mt-2"
                />
                <p className="text-xs text-gray-500 mt-1">Minimum 6 characters</p>
              </div>
              
              <div>
                <Label htmlFor="confirmPassword">Confirm Password</Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm new password"
                  className="mt-2"
                />
              </div>

              <div className="flex gap-3 mt-6">
                <Button
                  onClick={handleResetPassword}
                  disabled={actionLoading || !newPassword || !confirmPassword}
                  className="flex-1 bg-purple-600 hover:bg-purple-700"
                >
                  {actionLoading ? 'Resetting...' : 'Reset Password'}
                </Button>
                <Button
                  onClick={() => {
                    setShowPasswordModal(false);
                    setNewPassword('');
                    setConfirmPassword('');
                  }}
                  variant="outline"
                  className="flex-1"
                >
                  Cancel
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

export default UserDetails;
